import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../../core/api.service';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService
  ) { }

  searchBillingHistoryReport(request: BillingHistoryReportRequest): Observable<BillingHistoryReportRow[]> {
    return this.http.post<BillingHistoryReportRow[]>(
      this.apiService.buildUrl('api/report/billinghistory/search'),
      normalizeDateRange(request)
    );
  }

  getBillingInvoice(billingId: string, billingType: number): Observable<Blob> {
    return this.http.get(
      this.apiService.buildUrl(`api/report/billinginvoice/${billingStatusTypeName(billingType)}/${billingId}`),
      { responseType: 'blob' as const }
    );
  }

  getBillingInvoiceByNumber(invoiceNumber: string): Observable<Blob> {
    const params = new HttpParams().set('billingNumber', invoiceNumber);
    return this.http.get(
      this.apiService.buildUrl('api/report/billinginvoice'),
      { responseType: 'blob' as const, params }
    );
  }

  searchSalesOrderReport(request: SalesOrderReportRequest): Observable<SalesOrderReportRow[]> {
    return this.http.post<SalesOrderReportRow[]>(
      this.apiService.buildUrl('api/report/salesorder/search'),
      normalizeDateRange(request)
    );
  }

  searchRetrievalReport(request: RetrievalReportRequest): Observable<RetrievalReportRow[]> {
    return this.http.post<RetrievalReportRow[]>(
      this.apiService.buildUrl('api/report/customerpickup/search'),
      normalizeDateRange(request)
    );
  }

  searchDashboardPurchases(request: DashboardReportRequest): Observable<DashboardPurchaseRow[]> {
    return this.http.post<DashboardPurchaseRow[]>(
      this.apiService.buildUrl('api/report/dashboardsalesorder/search'),
      request
    );
  }

  searchDashboardRevenue(request: DashboardReportRequest): Observable<DashboardRevenueRow[]> {
    return this.http.post<DashboardRevenueRow[]>(
      this.apiService.buildUrl('api/report/dashboardretrievals/search'),
      request
    );
  }

  getBillingInvoiceUrl(billingId: string, billingType: number): string {
    return this.apiService.buildUrl(`api/report/billinginvoice/${billingStatusTypeName(billingType)}/${billingId}`);
  }

  getBillingHistoryExcel(billingId: string, billingType: number): Observable<BillingHistoryExcelRow[]> {
    return this.http.get<BillingHistoryExcelRow[]>(
      this.apiService.buildUrl(`api/report/billinghistoryexcel/${billingId}/${billingStatusTypeName(billingType)}`)
    );
  }

  searchBillingByText(searchValue: string): Observable<BillingSearchResult[]> {
    const end = new Date();
    const start = new Date();
    start.setFullYear(end.getFullYear() - 2);

    const request: BillingHistoryReportRequest = {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10)
    };

    const lowerQ = searchValue.trim().toLowerCase();

    return this.searchBillingHistoryReport(request).pipe(
      map((rows) =>
        (rows ?? [])
          .filter((r) =>
            (r.refNumber ?? '').toLowerCase().includes(lowerQ) ||
            (r.billingAcctName ?? '').toLowerCase().includes(lowerQ)
          )
          .map((r) => ({
            id: String(r.billingId ?? ''),
            acctName: r.billingAcctName ?? undefined,
            refNumber: r.refNumber ?? undefined,
            billingType: r.billingType ?? undefined,
            date: r.billingDate ?? undefined,
            totalAmount: r.amount ?? undefined
          } satisfies BillingSearchResult))
      )
    );
  }
}

export interface SalesOrderReportRequest {
  startDate?: string;
  endDate?: string;
  purchaseOrderFilter?: string;
  locationIds?: string[];
  productIds?: string[];
  salesOrderStatuses?: string[];
}

export interface SalesOrderReportRow {
  accountName?: string;
  locationName?: string;
  salesOrderNumber?: string;
  salesOrderStatus?: number;
  productName?: string;
  quantity?: number;
  unitPrice?: number;
  taxAmount?: number;
  dollarAmount?: number;
  invoiceNumber?: string;
  customerPo?: string;
  shipDateTime?: string;
  trailerNumber?: string;
  bolNumber?: string;
}

export interface RetrievalReportRequest {
  startDate?: string;
  endDate?: string;
  locationIds: string[];
  productIds: string[];
  customerPickupStatuses: string[];
}

export interface RetrievalReportRow {
  accountName?: string;
  locationName?: string;
  customerPickupNumber?: string;
  customerPickupStatus?: number;
  productName?: string;
  quantity?: number;
  unitPrice?: number;
  dollarAmount?: number;
  invoiceNumber?: string;
  shipDateTime?: string;
  trailerNumber?: string;
  bolNumber?: string;
}

export interface BillingHistoryReportRequest {
  startDate?: string;
  endDate?: string;
}

export interface BillingHistoryReportRow {
  billingId?: string;
  billingAcctName?: string;
  billingType?: number;
  billingDate?: string;
  creditAmount?: number;
  debtAmount?: number;
  amount?: number;
  refNumber?: string;
}

export interface DashboardReportRequest {
  startDate?: string;
  endDate?: string;
  purchaseOrderFilter?: string;
  locationIds: string[];
  productIds: string[];
  salesOrderStatuses: string[];
  isPricingReport: boolean;
}

export interface DashboardPurchaseRow {
  accountName?: string;
  locationName?: string;
  city?: string;
  state?: string;
  shipDateTime?: string;
  salesOrderStatus?: number;
  salesOrderStatusDesc?: string;
  salesOrderNumber?: string;
  bolNumber?: string;
  customerPo?: string;
  trailerNumber?: string;
  acctProductName?: string;
  mainProductName?: string;
  customerProductName?: string;
  internalProductName?: string;
  quantity?: number;
  unitPrice?: number;
  dollarAmount?: number;
  taxAmount?: number;
  invoiceNumber?: string;
}

export interface DashboardRevenueRow {
  accountName?: string;
  locationName?: string;
  city?: string;
  state?: string;
  cprActualDateTime?: string;
  cprGradeSheetStatus?: number;
  cprGradeSheetStatusDesc?: string;
  cprGradeSheetNumber?: string;
  acctProductName?: string;
  mainProductName?: string;
  spProductName?: string;
  quantity?: number;
  unitPrice?: number;
  dollarAmount?: number;
  taxAmount?: number;
  invoiceNumber?: string;
}

export interface BillingSearchResult {
  id: string;
  acctName?: string;
  refNumber?: string;
  billingType?: number;
  date?: string;
  totalAmount?: number;
}

const BILLING_STATUS_TYPE_NAMES: Record<number, string> = {
  0: 'SalesInvoice',
  1: 'VendorInvoice',
  2: 'ConsolidatedRecap',
  3: 'Credit',
  4: 'RebillInvoice'
};

function billingStatusTypeName(billingType: number): string {
  return BILLING_STATUS_TYPE_NAMES[billingType] ?? 'SalesInvoice';
}

export interface BillingHistoryExcelRow {
  consolidatedRecapNumber?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceFiscalPeriod?: string;
  customerReferenceNumber?: string;
  vendorInvoiceNumber?: string;
  account?: string;
  accountAddressLine1?: string;
  accountAddressCity?: string;
  accountAddressState?: string;
  accountAddressZip?: string;
  serviceProvider?: string;
  orderNumber?: string;
  serviceDate?: string;
  product?: string;
  productDescription?: string;
  unitPrice?: number;
  quantity?: number;
  lineTotal?: number;
}

function normalizeDateRange<T extends { startDate?: string; endDate?: string }>(request: T): T {
  const result = { ...request };
  if (result.startDate) {
    const d = new Date(result.startDate + 'T00:00:00');
    result.startDate = d.toISOString();
  }
  if (result.endDate) {
    const d = new Date(result.endDate + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    result.endDate = d.toISOString();
  }
  return result;
}
